import React, { useState, useEffect } from 'react';
import { authService } from '../services/auth';

export default function AgentCabinet({ onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('available');
  
  const [amount, setAmount] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestDetail, setRequestDetail] = useState(null);
  const [sellModal, setSellModal] = useState(false);
  const [sellPrice, setSellPrice] = useState('');
  const [sellMasterId, setSellMasterId] = useState('');

  const quickAmounts = [300, 500, 1000, 1500];

  useEffect(() => {
    loadData();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      setTimeout(() => {
        loadData(true);
        setSuccessMessage('Оплата успешна! Баланс обновлён.');
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 2000);
    }

    const interval = setInterval(() => {
      loadData(false, true);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const loadData = async (showLoading = false, silent = false) => {
    if (!silent && !showLoading) {
      setLoading(true);
    }
    if (showLoading) setRefreshing(true);
    if (!silent) setError('');

    try {
      const result = await authService.getCabinetData();
      setData(result);
      if (successMessage && showLoading) {
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Load data error:', err);
      if (!silent) setError('Ошибка загрузки: ' + err.message);
    } finally {
      setLoading(false);
      if (showLoading) setRefreshing(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    onLogout();
  };

  const handleQuickAmount = (val) => {
    setAmount(val.toString());
    setPayError('');
  };

  const handlePay = async () => {
    const payAmount = parseInt(amount);
    if (!payAmount || payAmount < 100) {
      setPayError('Минимальная сумма 100 руб.');
      return;
    }

    setPayLoading(true);
    setPayError('');

    try {
      const paymentUrl = await authService.getPaymentLink(payAmount);
      window.location.href = paymentUrl;
    } catch (err) {
      setPayError(err.message || 'Ошибка оплаты');
    } finally {
      setPayLoading(false);
    }
  };

  const handleBuyRequest = async (requestId) => {
    if (!window.confirm('Выкупить заявку за ' + data.requestPrice + ' руб.?')) return;

    try {
      await authService.buyRequest(requestId);
      setSuccessMessage('Заявка выкуплена!');
      loadData(true);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Ошибка выкупа');
    }
  };

  const handleSellRequest = async () => {
    if (!sellPrice || !sellMasterId) {
      setError('Укажите цену и ID мастера');
      return;
    }

    try {
      await authService.sellRequest(selectedRequest.id, sellMasterId, parseInt(sellPrice));
      setSuccessMessage('Заявка продана!');
      setSellModal(false);
      setSelectedRequest(null);
      loadData(true);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Ошибка продажи');
    }
  };

  const loadRequestDetail = async (requestId) => {
    setRequestLoading(true);
    try {
      const detail = await authService.getRequestDetail(requestId);
      setRequestDetail(detail);
    } catch (err) {
      setRequestDetail({ error: 'Ошибка загрузки заявки' });
    } finally {
      setRequestLoading(false);
    }
  };

  const closeRequestDetail = () => {
    setSelectedRequest(null);
    setRequestDetail(null);
    setSellModal(false);
  };

  const availableRequests = data?.availableRequests || [];
  const boughtRequests = data?.boughtRequests || [];

  if (loading) return <div style={styles.loading}>Загрузка...</div>;
  
  if (error && !data) return (
    <div style={styles.errorContainer}>
      <div style={styles.error}>{error}</div>
      <button onClick={() => loadData()} style={styles.retryBtn}>Повторить</button>
    </div>
  );

  if (!data) return null;

  // Модальное окно заявки
  if (selectedRequest) {
    const isBought = selectedRequest.masterId === data.agentId;

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <header style={styles.modalHeader}>
            <button onClick={closeRequestDetail} style={styles.closeBtn}>← Назад</button>
            <h2 style={styles.modalTitle}>Заявка #{selectedRequest.id}</h2>
            <div style={{ width: '80px' }}></div>
          </header>
          
          <div style={styles.modalBody}>
            {requestLoading ? (
              <div style={styles.loading}>Загрузка...</div>
            ) : requestDetail?.error ? (
              <div style={styles.error}>{requestDetail.error}</div>
            ) : requestDetail ? (
              <>
                <div style={styles.detailSection}>
                  <h3 style={styles.detailTitle}>Информация о заявке</h3>
                  <div style={styles.detailGrid}>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Номер заявки</span>
                      <span style={styles.detailValue}>{requestDetail.id}</span>
                    </div>
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Дата и время</span>
                      <span style={styles.detailValue}>{requestDetail.datetime}</span>
                    </div>
                    {isBought && (
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Телефон клиента</span>
                        <span style={styles.detailValue}>{requestDetail.clientPhone}</span>
                      </div>
                    )}
                    <div style={styles.detailItem}>
                      <span style={styles.detailLabel}>Статус</span>
                      <span style={{ ...styles.detailValue, ...styles.statusBadge(requestDetail.status) }}>
                        {getStatusText(requestDetail.status)}
                      </span>
                    </div>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Описание проблемы</span>
                    <p style={styles.detailDescription}>{requestDetail.description}</p>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Город</span>
                    <span style={styles.detailValue}>{requestDetail.city}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Адрес</span>
                    <span style={styles.detailValue}>{requestDetail.address}</span>
                  </div>
                  {isBought ? (
                    <div style={styles.boughtNotice}>
                      ✅ Заявка выкуплена. Телефон клиента доступен выше.
                    </div>
                  ) : (
                    <div style={styles.notBoughtNotice}>
                      🔒 Телефон клиента скрыт. Выкупите заявку, чтобы увидеть контакты.
                    </div>
                  )}
                </div>

                <div style={styles.detailSection}>
                  <h3 style={styles.detailTitle}>Диалог с AI-ассистентом</h3>
                  <div style={styles.chatContainer}>
                    {requestDetail.dialog?.map((msg, idx) => (
                      <div key={idx} style={{
                        ...styles.chatMessage,
                        ...(msg.role === 'user' ? styles.chatUser : styles.chatAI)
                      }}>
                        <div style={styles.chatAvatar}>
                          {msg.role === 'user' ? '👤' : '🤖'}
                        </div>
                        <div style={styles.chatBubble}>
                          <div style={styles.chatRole}>{msg.role === 'user' ? 'Клиент' : 'AI-ассистент'}</div>
                          <div style={styles.chatText}>{msg.message}</div>
                          <div style={styles.chatTime}>{msg.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.actionButtons}>
                  {!isBought && (
                    <button 
                      onClick={() => handleBuyRequest(selectedRequest.id)}
                      style={styles.buyBtn}
                    >
                      Выкупить за {data.requestPrice} ₽
                    </button>
                  )}
                  {isBought && (
                    <button 
                      onClick={() => setSellModal(true)}
                      style={styles.sellBtn}
                    >
                      Продать мастеру
                    </button>
                  )}
                </div>

                {sellModal && (
                  <div style={styles.sellModal}>
                    <h3 style={styles.sellTitle}>Продажа заявки</h3>
                    <label style={styles.label}>ID мастера</label>
                    <input
                      type="text"
                      value={sellMasterId}
                      onChange={(e) => setSellMasterId(e.target.value)}
                      style={styles.input}
                      placeholder="Например: 9924077374"
                    />
                    <label style={styles.label}>Цена продажи (руб.)</label>
                    <input
                      type="number"
                      value={sellPrice}
                      onChange={(e) => setSellPrice(e.target.value)}
                      style={styles.input}
                      placeholder="Например: 500"
                      min="1"
                    />
                    <div style={styles.sellActions}>
                      <button onClick={handleSellRequest} style={styles.confirmBtn}>Продать</button>
                      <button onClick={() => setSellModal(false)} style={styles.cancelBtn}>Отмена</button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button onClick={() => loadData(true)} style={styles.refreshBtn} title="Обновить">
          {refreshing ? '↻' : '⟳'}
        </button>
        <h1 style={styles.title}>Кабинет агента</h1>
        <div style={styles.headerActions}>
          <button onClick={handleLogout} style={styles.iconBtn} title="Выйти">
            <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      {successMessage && (
        <div style={styles.successBanner}>{successMessage}</div>
      )}

      <nav style={styles.tabs}>
        <button 
          onClick={() => setActiveTab('available')}
          style={activeTab === 'available' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
        >
          Доступные ({availableRequests.length})
        </button>
        <button 
          onClick={() => setActiveTab('bought')}
          style={activeTab === 'bought' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
        >
          Выкупленные ({boughtRequests.length})
        </button>
        <button 
          onClick={() => setActiveTab('balance')}
          style={activeTab === 'balance' ? { ...styles.tab, ...styles.tabActive } : styles.tab}
        >
          Баланс
        </button>
      </nav>

      <main style={styles.content}>
        {activeTab === 'available' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Доступные заявки</h2>
            {availableRequests.length === 0 ? (
              <div style={styles.empty}>Нет доступных заявок</div>
            ) : (
              <div style={styles.requestsList}>
                {availableRequests.map(req => (
                  <div 
                    key={req.id} 
                    style={styles.requestCard}
                    onClick={() => {
                      setSelectedRequest(req);
                      loadRequestDetail(req.id);
                    }}
                  >
                    <div style={styles.requestHeader}>
                      <span style={styles.requestId}>#{req.id}</span>
                      <span style={{ ...styles.requestStatus, ...styles.statusBadge(req.status) }}>
                        {getStatusText(req.status)}
                      </span>
                    </div>
                    <div style={styles.requestBody}>
                      <div style={styles.requestDate}>{req.datetime}</div>
                      <div style={styles.requestDescription}>{req.description}</div>
                      <div style={styles.requestCity}>📍 {req.city}</div>
                    </div>
                    <div style={styles.requestFooter}>
                      <span style={styles.requestPrice}>Цена: {data.requestPrice} ₽</span>
                      <span style={styles.tapHint}>Нажмите для просмотра</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'bought' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Выкупленные заявки</h2>
            {boughtRequests.length === 0 ? (
              <div style={styles.empty}>Нет выкупленных заявок</div>
            ) : (
              <div style={styles.requestsList}>
                {boughtRequests.map(req => (
                  <div 
                    key={req.id} 
                    style={styles.requestCard}
                    onClick={() => {
                      setSelectedRequest(req);
                      loadRequestDetail(req.id);
                    }}
                  >
                    <div style={styles.requestHeader}>
                      <span style={styles.requestId}>#{req.id}</span>
                      <span style={{ ...styles.requestStatus, ...styles.statusBadge('bought') }}>
                        Выкуплена
                      </span>
                    </div>
                    <div style={styles.requestBody}>
                      <div style={styles.requestDate}>{req.datetime}</div>
                      <div style={styles.requestDescription}>{req.description}</div>
                      <div style={styles.requestCity}>📍 {req.city}</div>
                    </div>
                    <div style={styles.requestFooter}>
                      <span style={styles.tapHint}>Нажмите для просмотра</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'balance' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Баланс</h2>
            
            <div style={styles.balanceCard}>
              <h3 style={styles.cardTitle}>Баланс</h3>
              <div style={styles.balanceAmount}>{data.balance} {data.currency}</div>
            </div>
            
            <div style={styles.topUpCard}>
              <label style={styles.label}>Сумма (руб.)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setPayError('');
                }}
                placeholder="Введите сумму"
                style={styles.input}
                min="100"
              />

              <div style={styles.quickAmounts}>
                {quickAmounts.map(val => (
                  <button
                    key={val}
                    onClick={() => handleQuickAmount(val)}
                    style={styles.quickBtn}
                  >
                    {val} ₽
                  </button>
                ))}
              </div>

              {payError && <div style={styles.payError}>{payError}</div>}

              <button
                onClick={handlePay}
                disabled={payLoading || !amount}
                style={{
                  ...styles.payBtn,
                  ...(payLoading || !amount ? styles.payBtnDisabled : {})
                }}
              >
                {payLoading ? 'Обработка...' : 'Оплатить'}
              </button>

              <div style={styles.notice}>
                Вы будете перенаправлены на ЮKassa для оплаты
              </div>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @media (max-width: 768px) {
          .header { padding: 16px !important; gap: 12px !important; }
          .title { font-size: 18px !important; }
          .tabs { padding: 12px 16px !important; gap: 6px !important; overflow-x: auto !important; flex-wrap: nowrap !important; }
          .tab { padding: 10px 12px !important; font-size: 13px !important; white-space: nowrap !important; }
          .content { padding: 16px !important; }
          .requestCard { padding: 16px !important; }
          .modalContent { margin: 10px !important; max-height: calc(100vh - 20px) !important; }
          .detailGrid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function getStatusText(status) {
  const statuses = {
    'new': 'Новая',
    'pending': 'Ожидает',
    'in_progress': 'В работе',
    'completed': 'Завершена',
    'closed': 'Закрыта',
    'bought': 'Выкуплена',
    'sold': 'Продана'
  };
  return statuses[status] || status;
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '20px 32px',
    background: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    gap: '16px'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#333',
    flex: 1
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  refreshBtn: {
    padding: '8px 12px',
    background: '#f0f0f0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '18px',
    minHeight: '44px'
  },
  iconBtn: {
    padding: '8px 12px',
    background: '#f0f0f0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '44px'
  },
  icon: {
    width: '20px',
    height: '20px',
    color: '#dc3545'
  },
  successBanner: {
    background: '#d4edda',
    color: '#155724',
    padding: '12px 32px',
    fontSize: '14px',
    textAlign: 'center',
    borderBottom: '1px solid #c3e6cb'
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    padding: '16px 32px',
    background: '#fff',
    borderBottom: '1px solid #ddd',
    flexWrap: 'wrap',
    overflowX: 'auto'
  },
  tab: {
    padding: '10px 16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
    borderBottom: '2px solid transparent',
    borderRadius: '4px',
    whiteSpace: 'nowrap'
  },
  tabActive: {
    color: '#0073aa',
    borderBottom: '2px solid #0073aa',
    background: '#f0f9ff'
  },
  content: {
    padding: '32px'
  },
  section: {
    background: '#fff',
    padding: '24px',
    borderRadius: '8px'
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '20px',
    color: '#333'
  },
  empty: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#999',
    fontSize: '16px'
  },
  requestsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  requestCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  requestId: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#0073aa'
  },
  requestStatus: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500'
  },
  requestBody: {
    marginBottom: '12px'
  },
  requestDate: {
    fontSize: '13px',
    color: '#999',
    marginBottom: '8px'
  },
  requestDescription: {
    fontSize: '15px',
    color: '#333',
    marginBottom: '8px',
    lineHeight: '1.4'
  },
  requestCity: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '4px'
  },
  requestFooter: {
    borderTop: '1px solid #eee',
    paddingTop: '12px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  requestPrice: {
    fontSize: '14px',
    color: '#22c55e',
    fontWeight: 'bold'
  },
  tapHint: {
    fontSize: '12px',
    color: '#999'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    overflow: 'auto'
  },
  modalContent: {
    background: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eee',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 1
  },
  closeBtn: {
    padding: '8px 16px',
    background: '#f0f0f0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#333'
  },
  modalBody: {
    padding: '24px'
  },
  detailSection: {
    marginBottom: '24px',
    background: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px'
  },
  detailTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    color: '#333',
    borderBottom: '2px solid #0073aa',
    paddingBottom: '8px'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '16px'
  },
  detailItem: {
    marginBottom: '12px'
  },
  detailLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#999',
    marginBottom: '4px',
    textTransform: 'uppercase'
  },
  detailValue: {
    fontSize: '15px',
    color: '#333',
    fontWeight: '500'
  },
  detailDescription: {
    fontSize: '15px',
    color: '#333',
    lineHeight: '1.6',
    margin: '8px 0 0 0',
    padding: '12px',
    background: '#fff',
    borderRadius: '4px',
    border: '1px solid #eee'
  },
  statusBadge: (status) => {
    const colors = {
      'new': { background: '#e3f2fd', color: '#1976d2' },
      'pending': { background: '#fff3e0', color: '#f57c00' },
      'in_progress': { background: '#e8f5e9', color: '#388e3c' },
      'completed': { background: '#e8f5e9', color: '#2e7d32' },
      'closed': { background: '#f5f5f5', color: '#616161' },
      'bought': { background: '#fff3e0', color: '#f57c00' },
      'sold': { background: '#e8f5e9', color: '#2e7d32' }
    };
    return colors[status] || { background: '#f5f5f5', color: '#616161' };
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '400px',
    overflowY: 'auto',
    padding: '12px',
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #eee'
  },
  chatMessage: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  chatUser: {
    flexDirection: 'row-reverse'
  },
  chatAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0
  },
  chatBubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: '12px',
    background: '#f5f5f5'
  },
  chatRole: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '4px',
    fontWeight: '500'
  },
  chatText: {
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.4',
    marginBottom: '6px'
  },
  chatTime: {
    fontSize: '11px',
    color: '#999',
    textAlign: 'right'
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'center'
  },
  buyBtn: {
    padding: '14px 32px',
    background: '#22c55e',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    minHeight: '48px'
  },
  sellBtn: {
    padding: '14px 32px',
    background: '#0073aa',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    minHeight: '48px'
  },
  sellModal: {
    background: '#f9f9f9',
    padding: '24px',
    borderRadius: '8px',
    marginTop: '24px',
    border: '1px solid #eee'
  },
  sellTitle: {
    margin: '0 0 16px 0',
    fontSize: '18px',
    color: '#333'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#666',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    minHeight: '48px'
  },
  sellActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px'
  },
  confirmBtn: {
    flex: 1,
    padding: '14px',
    background: '#22c55e',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    fontWeight: 'bold',
    minHeight: '48px'
  },
  cancelBtn: {
    flex: 1,
    padding: '14px',
    background: '#f0f0f0',
    color: '#666',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    minHeight: '48px'
  },
  balanceCard: {
    background: '#fff',
    padding: '32px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '24px'
  },
  cardTitle: {
    margin: '0 0 16px 0',
    fontSize: '18px',
    color: '#666'
  },
  balanceAmount: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#0073aa',
    margin: '16px 0'
  },
  topUpCard: {
    background: '#f9f9f9',
    padding: '24px',
    borderRadius: '8px',
    border: '1px solid #eee'
  },
  quickAmounts: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  quickBtn: {
    padding: '12px 20px',
    background: '#fff',
    border: '2px solid #0073aa',
    color: '#0073aa',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
    minHeight: '44px'
  },
  payBtn: {
    width: '100%',
    padding: '16px',
    background: '#22c55e',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '18px',
    cursor: 'pointer',
    fontWeight: 'bold',
    minHeight: '52px'
  },
  payBtnDisabled: {
    background: '#ccc',
    cursor: 'not-allowed'
  },
  payError: {
    color: '#dc3545',
    padding: '12px',
    background: '#fee',
    borderRadius: '4px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  notice: {
    marginTop: '16px',
    fontSize: '13px',
    color: '#999',
    textAlign: 'center'
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontSize: '18px'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px'
  },
  error: {
    color: '#dc3545',
    fontSize: '18px',
    padding: '20px',
    textAlign: 'center',
    marginBottom: '20px'
  },
  retryBtn: {
    padding: '12px 24px',
    background: '#0073aa',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  boughtNotice: {
    marginTop: '16px',
    padding: '12px',
    background: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    fontSize: '14px',
    textAlign: 'center'
  },
  notBoughtNotice: {
    marginTop: '16px',
    padding: '12px',
    background: '#fff3cd',
    color: '#856404',
    borderRadius: '4px',
    fontSize: '14px',
    textAlign: 'center'
  }
};